// Header propio del módulo Personal (RRHH).
// Mantiene la estética del header global SIGIF (gradiente primario) pero con menú propio.

import React, { useState, useEffect, useCallback } from 'react';
import {
    AppBar, Toolbar, Typography, IconButton, Tooltip, Badge,
    Menu, MenuItem, Avatar, Box, Divider,
} from '@mui/material';
import PeopleIcon from '@mui/icons-material/People';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import NotificationsIcon from '@mui/icons-material/Notifications';
import InboxIcon from '@mui/icons-material/MoveToInbox';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import HomeIcon from '@mui/icons-material/Home';
import { useNavigate } from 'react-router-dom';
import NotificacionesPanel from '../../components/NotificacionesPanel';
import BandejaEntradaPanel from '../../components/BandejaEntradaPanel';
import MisTareasPanel from '../../components/MisTareasPanel';
import PersonalQuickSearch from './PersonalQuickSearch';
import { notificacionesListarJWT, pasesBandejaCountJWT } from '../../api/endpoints';
import { nodeApiPath } from '../../utils/paths';

function fotoUrl(idPersonal) {
    if (!idPersonal) return null;
    const token = localStorage.getItem('SIGIF_JWT_TOKEN');
    return `${nodeApiPath(`personal/${idPersonal}/foto`)}?token=${token}`;
}

const PRIMARY = '#0277bd';
const PRIMARY_DARK = '#01579b';

export default function PersonalHeader({ usuario, onSelectPersona }) {
    const navigate = useNavigate();

    const [anchorEl, setAnchorEl] = useState(null);
    const [avatarSrc, setAvatarSrc] = useState(null);
    const [avatarError, setAvatarError] = useState(false);

    const [notifOpen, setNotifOpen] = useState(false);
    const [noLeidas, setNoLeidas] = useState(0);
    const [bandejaOpen, setBandejaOpen] = useState(false);
    const [pasesPendientes, setPasesPendientes] = useState(0);
    const [tareasOpen, setTareasOpen] = useState(false);
    const [tareasCount, setTareasCount] = useState(0);

    const getInitials = (nombre) => {
        if (!nombre) return '?';
        const parts = nombre.trim().split(' ');
        if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
        return nombre.substring(0, 2).toUpperCase();
    };

    const capitalize = (s) => (s || '').toLowerCase().split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');

    const [fotoVersion, setFotoVersion] = useState(Date.now());
    useEffect(() => {
        if (usuario?.dni) {
            setAvatarSrc(`${fotoUrl(usuario.id_personal || usuario.dni)}&v=${fotoVersion}`);
            setAvatarError(false);
        }
    }, [usuario, fotoVersion]);

    useEffect(() => {
        const handler = () => setFotoVersion(Date.now());
        window.addEventListener('sigif-foto-actualizada', handler);
        return () => window.removeEventListener('sigif-foto-actualizada', handler);
    }, []);

    const cargarNoLeidas = useCallback(async () => {
        try { const r = await notificacionesListarJWT(); if (r?.ok) setNoLeidas(r.noLeidas || 0); } catch { }
    }, []);
    const cargarPases = useCallback(async () => {
        try { const r = await pasesBandejaCountJWT(); if (r?.ok) setPasesPendientes(r.count || 0); } catch { }
    }, []);
    const cargarTareas = useCallback(async () => {
        try {
            const token = localStorage.getItem('SIGIF_JWT_TOKEN');
            if (!token) return;
            const res = await fetch(nodeApiPath('personal/bandeja/mis-tareas'), { headers: { Authorization: `Bearer ${token}` } });
            if (!res.ok) return;
            const data = await res.json();
            if (data?.ok) setTareasCount((data.items || []).length);
        } catch { }
    }, []);

    useEffect(() => {
        cargarNoLeidas(); cargarPases(); cargarTareas();
        const i = setInterval(() => { cargarNoLeidas(); cargarPases(); cargarTareas(); }, 30000);
        return () => clearInterval(i);
    }, [cargarNoLeidas, cargarPases, cargarTareas]);

    return (
        <>
            <AppBar position="fixed" elevation={2}
                sx={{
                    zIndex: (theme) => theme.zIndex.drawer + 1,
                    background: `linear-gradient(90deg, ${PRIMARY_DARK} 0%, ${PRIMARY} 100%)`,
                }}>
                <Toolbar>
                    <PeopleIcon sx={{ mr: 1.5 }} />
                    <Typography variant="h6" sx={{ fontWeight: 500, fontSize: 18, mr: 3 }}>
                        SIGIF · Recursos Humanos
                    </Typography>

                    {/* Buscador de personas */}
                    <Box sx={{ flex: 1, maxWidth: 420, mr: 2 }}>
                        <PersonalQuickSearch onSelect={onSelectPersona} variant="dark" />
                    </Box>

                    <Box sx={{ flex: 1 }} />

                    {/* Mis tareas */}
                    {tareasCount > 0 && (
                        <Tooltip title="Mis tareas pendientes" arrow>
                            <IconButton color="inherit" onClick={() => setTareasOpen(true)} size="large">
                                <Badge badgeContent={tareasCount} color="error">
                                    <AssignmentTurnedInIcon />
                                </Badge>
                            </IconButton>
                        </Tooltip>
                    )}

                    {/* Bandeja pases */}
                    <Tooltip title="Bandeja de entrada" arrow>
                        <IconButton color="inherit" onClick={() => setBandejaOpen(true)} size="large">
                            <Badge badgeContent={pasesPendientes} color="secondary">
                                <InboxIcon />
                            </Badge>
                        </IconButton>
                    </Tooltip>

                    {/* Notificaciones */}
                    <Tooltip title="Notificaciones" arrow>
                        <IconButton color="inherit" onClick={() => setNotifOpen(true)} size="large">
                            <Badge badgeContent={noLeidas} color="secondary">
                                <NotificationsIcon />
                            </Badge>
                        </IconButton>
                    </Tooltip>

                    {/* Usuario */}
                    <Box onClick={(e) => setAnchorEl(e.currentTarget)}
                        sx={{ ml: 2, display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer' }}>
                        <Avatar
                            src={!avatarError && avatarSrc ? avatarSrc : undefined}
                            onError={() => setAvatarError(true)}
                            sx={{ width: 36, height: 36, border: '2px solid rgba(255,255,255,0.8)', fontSize: 14, fontWeight: 600 }}
                        >
                            {(avatarError || !avatarSrc) && getInitials(usuario?.nombre)}
                        </Avatar>
                        <ArrowDropDownIcon />
                    </Box>
                    <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)} keepMounted>
                        <MenuItem disabled><strong>{capitalize(usuario?.nombre)}</strong></MenuItem>
                        <MenuItem onClick={() => { setAnchorEl(null); navigate('/'); }}>
                            <HomeIcon fontSize="small" style={{ marginRight: 8 }} />
                            Volver a SIGIF
                        </MenuItem>
                        <MenuItem onClick={() => { setAnchorEl(null); navigate('/perfil'); }}>
                            <AccountCircleIcon fontSize="small" style={{ marginRight: 8 }} />
                            Mi Perfil
                        </MenuItem>
                        <Divider />
                        <MenuItem onClick={() => {
                            setAnchorEl(null);
                            localStorage.removeItem('SIGIF_JWT_TOKEN');
                            window.location.href = '/login';
                        }}>
                            <ExitToAppIcon fontSize="small" style={{ marginRight: 8 }} />
                            Cerrar sesión
                        </MenuItem>
                    </Menu>
                </Toolbar>
            </AppBar>

            {/* Paneles asociados */}
            <NotificacionesPanel open={notifOpen} onClose={() => { setNotifOpen(false); cargarNoLeidas(); }} />
            <BandejaEntradaPanel open={bandejaOpen} onClose={() => { setBandejaOpen(false); cargarPases(); }} />
            <MisTareasPanel open={tareasOpen} onClose={() => { setTareasOpen(false); cargarTareas(); }} onChange={cargarTareas} />
        </>
    );
}
